import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { SectionCard } from "shared/app/SectionCard";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  floatParser,
  moneyCentsParser
} from "shared/components/forms/BaseInput/parsers";
import { type Register } from "shared/hooks";
import { DateInput, Select, Show } from "shared/components";
import { WaitlistToggle } from "./WaitlistToggle";
import {
  APPFOLIO_FEE_MAX_CENTS,
  PURCHASE_PRICE_PMS_SYNCED,
  PURCHASE_PRICE_PMS_UNSYNCED,
  SALE_PRICE_PMS_SYNCED,
  SALE_PRICE_PMS_UNSYNCED
} from "./constants";
import { formatToCurrency } from "shared/utils";
import { UnitRentField } from "./components/RentField";
import { PmsSyncOption } from "./components/PmsSyncOption";
import { PMSValue } from "./components/PmsValue";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

const statusOptions = [
  { value: false, label: "Available" },
  { value: true, label: "Occupied" }
];

const priceParser = floatParser({ maxDecimals: 2 });

type PmsFeeValue = number | { cents?: number; currency?: string } | null;

interface PmsValues {
  sale_price?: { cents: number; currency: string };
  purchase_price?: { cents: number; currency: string };
  security_deposit?: PmsFeeValue;
  application_fee?: PmsFeeValue;
}

interface PmsOverrides {
  sale_price?: boolean;
  purchase_price?: boolean;
}

export type HandleChange = (
  key: string,
  customMap?: (value: unknown, prev: unknown) => unknown,
  mask?: string
) => (e?: unknown) => void;

interface RentFieldsProps {
  homeSiteRentEnabled: boolean;
  handleChange: HandleChange;
  values: Record<string, unknown>;
  register: Register;
  unitPmsId?: string;
}

const RentFields = ({
  homeSiteRentEnabled,
  handleChange,
  values,
  register,
  unitPmsId
}: RentFieldsProps) => {
  if (homeSiteRentEnabled)
    return (
      <>
        <UnitRentField
          handleChange={handleChange}
          values={values}
          register={register}
          rentType="home"
          id="homeRentAmount"
          label="Home rent"
          unitPmsId={unitPmsId}
        />
        <UnitRentField
          handleChange={handleChange}
          values={values}
          register={register}
          rentType="site"
          id="siteRentAmount"
          label="Site rent"
          unitPmsId={unitPmsId}
        />
      </>
    );

  return (
    <UnitRentField
      handleChange={handleChange}
      values={values}
      register={register}
      rentType="monthly"
      id="monthlyRentAmount"
      label="Rent amount"
      unitPmsId={unitPmsId}
    />
  );
};

const toFeeCents = (value: PmsFeeValue): number | null => {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && typeof value.cents === "number")
    return value.cents;

  return null;
};

const formatCentsLabel = (cents: number | null) =>
  cents != null ? formatToCurrency({ cents, currency: "USD" }) : "";

const PmsFeeField = ({
  id,
  label,
  name,
  fieldKey,
  overriddenKey,
  pmsCents,
  unitPmsId,
  register
}: {
  id: string;
  label: string;
  name: string;
  fieldKey: string;
  overriddenKey: string;
  pmsCents: number | null;
  unitPmsId?: string;
  register: Register;
}) => {
  const field = register(fieldKey);
  const overriddenField = register(overriddenKey);
  const synced = !overriddenField.value;
  const readOnly = Boolean(unitPmsId && synced);

  return (
    <div className="grid grid-cols-2 gap-4 items-center">
      <div className="money-input-2">
        <BaseInput
          readOnly={readOnly}
          id={id}
          label={label}
          prefix="$"
          parser={moneyCentsParser({
            max: APPFOLIO_FEE_MAX_CENTS,
            trimZeroCents: true
          })}
          className={readOnly ? "[&_.bg-white]:!bg-alpha-40" : ""}
          value={field.value ?? null}
          onChange={(val) => field.onChange(val)}
        />
        {unitPmsId ? (
          <PmsSyncOption
            value={synced}
            onChange={(nextSynced) => {
              overriddenField.onChange(!nextSynced);
              if (nextSynced) field.onChange(pmsCents);
            }}
            name={name}
          />
        ) : null}
      </div>
      <PMSValue value={formatCentsLabel(pmsCents)} />
    </div>
  );
};

interface PmsPriceFieldProps {
  id: string;
  fieldKey: string;
  label: string;
  syncOptionName: string;
  overrideKey: keyof PmsOverrides;
  pmsValueKey: keyof Pick<PmsValues, "sale_price" | "purchase_price">;
  syncedTooltip: string;
  unsyncedTooltip: string;
  values: Record<string, unknown>;
  register: Register;
  unitPmsId?: string;
  showSyncOption: boolean;
}

const PmsPriceField = ({
  id,
  fieldKey,
  label,
  syncOptionName,
  overrideKey,
  pmsValueKey,
  syncedTooltip,
  unsyncedTooltip,
  values,
  register,
  unitPmsId,
  showSyncOption
}: PmsPriceFieldProps) => {
  const synced = (values.pms_overrides as PmsOverrides)?.[overrideKey] ?? true;
  const readOnly = Boolean(unitPmsId && synced);
  const pmsVal = (values.pms_values as PmsValues)?.[pmsValueKey];

  return (
    <div className="grid grid-cols-2 gap-4 items-center">
      <div className="money-input-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <BaseInput
              readOnly={readOnly}
              id={id}
              label={label}
              placeholder={readOnly ? "-" : undefined}
              prefix="$"
              parser={priceParser}
              className={readOnly ? "[&_.bg-white]:!bg-alpha-40" : ""}
              value={register(fieldKey).value ?? null}
              onChange={(val) => register(fieldKey).onChange(val)}
            />
          </TooltipTrigger>
          {unitPmsId ? (
            <TooltipContent className="max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
              <p>{synced ? syncedTooltip : unsyncedTooltip}</p>
            </TooltipContent>
          ) : null}
        </Tooltip>
        {unitPmsId && showSyncOption ? (
          <PmsSyncOption
            value={synced}
            onChange={(value) => {
              const overrides: PmsOverrides = {
                ...((values.pms_overrides as PmsOverrides) || {}),
                [overrideKey]: value
              };
              register("pms_overrides").onChange(overrides);
              if (value)
                register(fieldKey).onChange(pmsVal ? pmsVal.cents / 100 : 0);
            }}
            name={syncOptionName}
          />
        ) : null}
      </div>
      <PMSValue
        value={pmsVal ? `$${(pmsVal.cents / 100).toLocaleString()}` : ""}
      />
    </div>
  );
};

interface PMSRentalInfoFormProps {
  unitPmsId?: string;
  handleChange: HandleChange;
  values: Record<string, unknown>;
  register: Register;
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
}

export const PMSRentalInfoForm = ({
  unitPmsId,
  handleChange,
  values,
  register,
  headerExtra,
  footer
}: PMSRentalInfoFormProps) => {
  const { pms, type, permissions } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const pmsFeeValues = values.pms_values as PmsValues | undefined;

  return (
    <SectionCard
      title="Rental information"
      headerExtra={headerExtra}
      footer={footer}
    >
      <div className="space-y-8">
        <div className="space-y-8">
          <RentFields
            homeSiteRentEnabled={homeSiteRentEnabled}
            handleChange={handleChange}
            values={values}
            register={register}
            unitPmsId={unitPmsId}
          />

          <PmsPriceField
            id="salePrice"
            fieldKey="sale_price"
            label="Sale price"
            syncOptionName="salePrice"
            overrideKey="sale_price"
            pmsValueKey="sale_price"
            syncedTooltip={SALE_PRICE_PMS_SYNCED}
            unsyncedTooltip={SALE_PRICE_PMS_UNSYNCED}
            values={values}
            register={register}
            unitPmsId={unitPmsId}
            showSyncOption={type.isBoomScreen}
          />

          <PmsPriceField
            id="purchasePrice"
            fieldKey="purchase_price"
            label="Purchase price"
            syncOptionName="purchasePrice"
            overrideKey="purchase_price"
            pmsValueKey="purchase_price"
            syncedTooltip={PURCHASE_PRICE_PMS_SYNCED}
            unsyncedTooltip={PURCHASE_PRICE_PMS_UNSYNCED}
            values={values}
            register={register}
            unitPmsId={unitPmsId}
            showSyncOption={type.isBoomScreen}
          />

          <Show if={pms.isAppfolio}>
            <PmsFeeField
              id="securityDepositCents"
              label="Security deposit"
              name="securityDeposit"
              fieldKey="security_deposit_cents"
              overriddenKey="security_deposit_overridden"
              pmsCents={toFeeCents(pmsFeeValues?.security_deposit ?? null)}
              unitPmsId={unitPmsId}
              register={register}
            />
            <PmsFeeField
              id="applicationFeeCents"
              label="Application fee"
              name="applicationFee"
              fieldKey="application_fee_cents"
              overriddenKey="application_fee_overridden"
              pmsCents={toFeeCents(pmsFeeValues?.application_fee ?? null)}
              unitPmsId={unitPmsId}
              register={register}
            />
          </Show>
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <Tooltip>
              <TooltipTrigger className="w-full text-left">
                <Select
                  id="current-status"
                  label="Current status"
                  options={statusOptions}
                  required
                  {...register("occupied")}
                  isDisabled={
                    !permissions.canChangeAvailablebyDate ||
                    Boolean(unitPmsId && values.pms_occupied_override)
                  }
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {values.pms_available_at_override
                    ? `Current status & available by date is synchronized with PMS.
                  You can manually edit these fields by disabling
                  synchronization for this field. To do this, click the 'Edit manually' option.`
                    : `Current status & available by date is currently edited manually.
                You can enable synchronization by clicking 'Sync with PMS' option`}
                </p>
              </TooltipContent>
            </Tooltip>
            <PmsSyncOption
              className="mt-4"
              name={register("pms_occupied_override").name}
              value={register("pms_occupied_override").value}
              onChange={(value) => {
                register("pms_occupied_override").onChange(value);
                register("pms_available_at_override").onChange(value);

                if (value) {
                  register("occupied").onChange(values.pms_occupied);
                  register("available_at").onChange(values.pms_available_at);
                }
              }}
            />
          </div>
          <PMSValue
            value={register("pms_occupied").value ? "Occupied" : "Available"}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full text-left">
                  <DateInput
                    id="available_at"
                    label="Available by date"
                    required
                    clearable
                    readOnly={
                      !permissions.canChangeAvailablebyDate ||
                      Boolean(unitPmsId && values.pms_available_at_override)
                    }
                    {...register("available_at")}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {values.pms_available_at_override
                    ? `Current status & available by date is synchronized with PMS.
                  You can manually edit these fields by disabling
                  synchronization for this field. To do this, click the 'Edit manually' option.`
                    : `Current status & available by date is currently edited manually.
                You can enable synchronization by clicking 'Sync with PMS' option`}
                </p>
              </TooltipContent>
            </Tooltip>
            <PmsSyncOption
              name={register("pms_available_at_override").name}
              value={register("pms_available_at_override").value}
              onChange={(value) => {
                register("pms_occupied_override").onChange(value);
                register("pms_available_at_override").onChange(value);

                if (value) {
                  register("occupied").onChange(values.pms_occupied);
                  register("available_at").onChange(values.pms_available_at);
                }
              }}
            />
          </div>
          <PMSValue
            value={
              values.pms_available_at
                ? moment(String(values.pms_available_at))
                    .utc()
                    .format("MMM D, YYYY")
                : "N/A"
            }
          />
        </div>

        <div className="border-t border-alpha-40">
          <WaitlistToggle
            register={register}
            occupied={values.occupied as boolean}
            availableAt={values.available_at as string | null}
            canChange={permissions.canChangeAvailablebyDate}
          />
        </div>
      </div>
    </SectionCard>
  );
};
